import {
  Button,
  ButtonMenu,
  Chip,
  CountBadge,
  Css,
  HbLoadingSpinner,
  IconButton,
  Palette,
  Tooltip,
  useModal,
  useRightPane,
  useTestIds,
} from "@homebound/beam";
import { Fragment, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  createDesignPackageAddToSlotUrl,
  createDesignPackageProductCardUrl,
  createDesignPackageProductSearchUrl,
  createDesignPackageUrl,
  createEditPersonalizationOptionUrl,
  createMaterialDetailsUrl,
  createPlanPackageUrl,
} from "src/RouteUrls";
import { Icon } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  FeatureFlagType,
  ReadyPlanOptionGroupPersonalizationType,
  TakeoffLineItemDesignPackageType,
} from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { AddToOptionModal } from "src/routes/libraries/design-catalog/design-package-configurator/components/AddToOptionModal";
import {
  DesignPackageConfiguratorSlotFragment,
  DesignPackageItemSlotEditorDocument,
  DesignPackageTliCardFragment,
  useDesignPackageItemSlotEditor_DeleteTliMutation,
} from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import { DesignPackageSlotsDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageSlotsLeftNav.generated";
import { AddNewDesignPackageSlotModal } from "src/routes/libraries/design-catalog/design-package-configurator/routes/new/AddNewDesignPackageSlotModal";
import { fail, noop } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { useDesignPackageConfiguratorContext } from "../DesignPackageConfiguratorContext";
import { CopyDesignPackageCardsModal } from "./CopyDesignPackageCardsModal";
import { DesignPackageDesignNotePane } from "./DesignPackageDesignNotePane";
import {
  productCardHoverIcons,
  productCardHoverImageOverlay,
  productCardHoverScope,
} from "./DesignPackageItemSlotEditor.css";
import "./DesignPackageItemSlotEditor.css.ts";
import { OptionCombinationsChip } from "./OptionCombinationsChip";

/** The Grid of available TLIs/Cards for the selected Slot that can be configured. */
export function DesignPackageItemSlotEditor() {
  const { designPackage, selectedSlots, loading, readOnly, setCtxState } = useDesignPackageConfiguratorContext();
  const [deleteTli] = useDesignPackageItemSlotEditor_DeleteTliMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const tid = useTestIds({});
  const { openModal } = useModal();
  const { openRightPane, closeRightPane } = useRightPane();
  // Close design note's pane when slots change
  useEffect(() => closeRightPane(), [selectedSlots, closeRightPane]);

  const singleSlot = selectedSlots?.length === 1 ? selectedSlots.first! : undefined;
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);

  if (!selectedSlots?.nonEmpty)
    return (
      <div css={Css.h100.df.fdc.jcc.aic.p4.gap3.$}>
        {loading ? (
          <HbLoadingSpinner />
        ) : (
          <>
            <div css={Css.xl2.$}>👈 Please select a slot or some filters ☝️ to configure</div>
            {designerModifiedSlotsEnabled && (
              <Button
                label="Add a new slot?"
                onClick={() =>
                  openModal({
                    content: (
                      <AddNewDesignPackageSlotModal
                        designPackageId={designPackage.id}
                        rpavId={designPackage.version.id}
                        setCtxState={setCtxState}
                      />
                    ),
                    size: "md",
                  })
                }
                variant="secondary"
              />
            )}
          </>
        )}
      </div>
    );

  function handleRedirect(tli: DesignPackageTliCardFragment, correctPlaceholderId: string) {
    navigate(
      tli.materialVariant || tli.isDisabledMaterialVariant
        ? createDesignPackageProductCardUrl(
            designPackage.id,
            designPackage.version.id,
            correctPlaceholderId,
            tli.id,
            tli.isDisabledMaterialVariant ? "noMaterialVariant" : tli.materialVariant?.id,
          )
        : createDesignPackageProductSearchUrl(designPackage.id, designPackage.version.id, correctPlaceholderId, tli.id),
    );
  }

  // We might have several slots in view, that have:
  //
  // - regular placeholders (which get their own row/slot)
  // - regular product cards for those placeholders
  // - shadow placeholders that are actually controlling the placeholder
  // - shadow product cards
  //
  // All intermixed. So to simplify things, we do this "ugly" but efficient up-front pass through
  // the TLIs to tease out which is which.
  const slotToRegularPlaceholders: Record<string, DesignPackageTliCardFragment[]> = {};
  const placeholderToProducts: Record<string, DesignPackageTliCardFragment[]> = {};
  selectedSlots?.forEach((dpSlot) => {
    dpSlot.items.forEach((tli) => {
      if (tli.placeholder) {
        // Push all cards (for either regular placeholders or shadows) into this map
        (placeholderToProducts[tli.placeholder.id] ??= []).push(tli);
      } else {
        // Only regular placeholders should drive the main UI, the shadows stay in the background
        if (tli.designPackageType?.code === TakeoffLineItemDesignPackageType.RegularPlaceholder) {
          (slotToRegularPlaceholders[dpSlot.slot.id] ??= []).push(tli);
        } else {
          // This is the DesignPackageTliCardFragment for a shadow placeholder, which
          // currently we don't need (we just use its id for map lookups), so ignore for now.
        }
      }
    });
  });

  return (
    <div css={Css.df.fdc.gap2.$}>
      {selectedSlots.map((dpSlot) => (
        <Fragment key={dpSlot.slot.id}>
          {slotToRegularPlaceholders[dpSlot.slot.id]?.map((placeholderTli) => {
            // If this Placeholder is controlled by a ShadowPlaceholder, swap out "my" products for the Shadow's products.
            const maybeShadowTli = placeholderTli.personalizationShadowPlaceholder;
            const isSingleProductPersonalizationOption =
              maybeShadowTli?.personalizationOptionGroup?.personalizationType?.code ===
              ReadyPlanOptionGroupPersonalizationType.SingleProduct;
            // Which Placeholder to use. Often propagates to the URL which becomes part of New Product Card mutations. Sometimes
            // (such as for TLI.planLines modals) we still need the original placeholder ID, not the shadow.
            const correctPlaceholder =
              // We want to use the original placeholder for SingleProduct personalization options, since product cards from that placeholder remain active
              isSingleProductPersonalizationOption ? placeholderTli : (maybeShadowTli ?? placeholderTli);
            const correctPlaceholderId = correctPlaceholder.id;
            const productCards = placeholderToProducts[correctPlaceholderId] ?? [];

            return (
              <div key={placeholderTli.id} css={Css.df.fdc.gap2.bb.bcGray300.pb3.mb1.$} {...tid.familyHeader}>
                <div css={Css.df.jcsb.$}>
                  <div css={Css.df.fdr.aic.gap1.$} {...tid.slotheader}>
                    <div css={Css.smSb.if(!dpSlot.isFullyAssigned).red600.$}>
                      {placeholderTli.customPlaceholderName || placeholderTli.name}
                      {maybeShadowTli &&
                        // e.g. "(Personalized by Linked Product Option)"
                        ` (Personalized by ${maybeShadowTli.personalizationOptionGroup?.personalizationType?.name ?? "Personalization"}${
                          maybeShadowTli.personalizationOptionGroup?.groupByFeature
                            ? ` - Grouped by feature: ${maybeShadowTli.location.displayLocationPath})`
                            : ")"
                        }`}
                    </div>
                    {/* Display a red dot if any of the slots in the subgroup are not 100% covered */}
                    {!dpSlot.isFullyAssigned && (
                      <Icon icon="circle" {...tid.incompleteIndicator} inc={1.5} color={Palette.Red500} />
                    )}
                    {placeholderTli.materialVariant?.materialAttributeValues
                      .groupByObject((mav) => mav.dimension.name)
                      .map(([dimensionName, mavs]) => (
                        <Chip
                          key={dimensionName}
                          text={`${dimensionName}: ${mavs.map((mav) => mav.valueWithUom).join(" • ")}`}
                        />
                      ))}
                  </div>
                  <div css={Css.df.gap2.$}>
                    <div css={Css.df.aic.gap0.$}>
                      <CountBadge count={placeholderTli.designNotes.length} hideIfZero />
                      <IconButton
                        icon="pencil"
                        tooltip="Edit Design Notes"
                        onClick={() => {
                          openRightPane({ content: <DesignPackageDesignNotePane placeholder={placeholderTli} /> });
                        }}
                      />
                    </div>
                    <ButtonMenu
                      trigger={{ icon: "plus" }}
                      // Only allow to create/add to existing personalization options if the design package is editable
                      disabled={!maybeShadowTli && !designPackage.canEdit.allowed}
                      items={
                        maybeShadowTli
                          ? [
                              {
                                label: `${!designPackage.canEdit.allowed ? "View" : "Edit"} Personalization Options`,
                                onClick: createEditPersonalizationOptionUrl(
                                  designPackage.id,
                                  designPackage.version.id,
                                  maybeShadowTli.personalizationOptionGroup?.id ??
                                    fail("no p13n rpog on shadow placeholder"),
                                  undefined,
                                  returnTo,
                                ),
                              },
                            ]
                          : [
                              {
                                label: "Create Personalization Option",
                                onClick: createEditPersonalizationOptionUrl(
                                  designPackage.id,
                                  designPackage.version.id,
                                  "add",
                                  correctPlaceholderId,
                                  returnTo,
                                ),
                              },
                              {
                                label: "Add to Existing",
                                onClick: () => {
                                  openModal({
                                    content: (
                                      <AddToOptionModal
                                        designPackageId={designPackage.id}
                                        designPackageVersionId={designPackage.version.id}
                                        placeholderTliId={correctPlaceholderId}
                                      />
                                    ),
                                  });
                                },
                              },
                            ]
                      }
                    />
                    <IconButton
                      icon="duplicate"
                      onClick={() =>
                        openModal({
                          content: (
                            <CopyDesignPackageCardsModal
                              designPackageId={designPackage.id}
                              designPackageVersionId={designPackage.version.id}
                              placeholderTli={maybeShadowTli ?? placeholderTli}
                            />
                          ),
                        })
                      }
                    />
                    <IconButton
                      icon="trash"
                      disabled={readOnly || !placeholderTli.canDeleteFromDesignPackage?.allowed}
                      tooltip={placeholderTli.canDeleteFromDesignPackage?.disabledReasons.first?.message}
                      onClick={() => {
                        void deleteTli({
                          variables: { readyPlanId: designPackage.id, input: { id: placeholderTli.id, active: false } },
                          refetchQueries: [DesignPackageSlotsDocument, DesignPackageItemSlotEditorDocument],
                        });
                      }}
                    />
                  </div>
                </div>
                <InlineGrid>
                  {tliFilterAndSortToFinal(productCards).map((tli) => (
                    <div
                      key={tli.id}
                      className={productCardHoverScope}
                      {...tid.productCard}
                      css={Css.bgWhite.br8.p2.df.fdc.gap2.relative.h100.bshBasic.onHover.bshHover.$}
                    >
                      <div
                        className={productCardHoverIcons}
                        css={Css.absolute.o0.vh.top0.right3.df.fdc.gap1.z3.add("transition", "all 250ms").$}
                        onClick={(e) => e.stopPropagation()} // prevent click from bubbling up to the overall card's onClick
                      >
                        {!readOnly && (
                          <IconButton
                            icon="refresh"
                            color={
                              !tli.materialVariant || tli.isDisabledMaterialVariant ? Palette.Gray700 : Palette.White
                            }
                            onClick={createDesignPackageProductSearchUrl(
                              designPackage.id,
                              designPackage.version.id,
                              correctPlaceholderId,
                              tli.id,
                            )}
                          />
                        )}
                        {(tli.materialVariant?.id || tli.isDisabledMaterialVariant) && (
                          <IconButton
                            icon="pencil"
                            color={
                              !tli.materialVariant || tli.isDisabledMaterialVariant ? Palette.Gray700 : Palette.White
                            }
                            onClick={createDesignPackageProductCardUrl(
                              designPackage.id,
                              designPackage.version.id,
                              correctPlaceholderId,
                              tli.id,
                              tli.isDisabledMaterialVariant ? "noMaterialVariant" : tli.materialVariant?.id,
                            )}
                          />
                        )}
                        {tli.materialVariant?.id && (
                          <Link to={createMaterialDetailsUrl(tli.materialVariant.id)} target="_blank">
                            <IconButton
                              icon="linkExternal"
                              tooltip="View in Material Catalog"
                              color={Palette.White}
                              onClick={noop}
                            />
                          </Link>
                        )}
                        {!readOnly && (
                          <IconButton
                            icon="trash"
                            color={
                              !tli.materialVariant || tli.isDisabledMaterialVariant ? Palette.Gray700 : Palette.White
                            }
                            onClick={() => {
                              const targetGroups = tli.placeholder?.targetGroups.sortBy((rpog) => rpog.order) ?? [];
                              const groupsMessage = targetGroups.nonEmpty
                                ? `The ${targetGroups.map((rpog) => rpog.name).join(" / ")} associated with this slot will no longer be filled by a product.`
                                : "";
                              return openModal({
                                content: (
                                  <ConfirmationModal
                                    title="Remove Product?"
                                    label="Remove"
                                    confirmationMessage={`Are you sure you want to delete this card? ${groupsMessage}`}
                                    onConfirmAction={async () => {
                                      const result = await deleteTli({
                                        variables: {
                                          readyPlanId: designPackage.id,
                                          input: { id: tli.id, active: false },
                                        },
                                        refetchQueries: [
                                          DesignPackageSlotsDocument,
                                          DesignPackageItemSlotEditorDocument,
                                        ],
                                      });
                                      const params = new URLSearchParams();
                                      selectedSlots.forEach((s) => params.append("slotIds[]", s.slot.id));
                                      const versionId = result.data?.saveTakeoffLineItems.version.id ?? fail();
                                      navigate(`${createDesignPackageUrl(designPackage.id, versionId)}?${params}`, {
                                        replace: true,
                                      });
                                    }}
                                  />
                                ),
                              });
                            }}
                          />
                        )}
                      </div>
                      {tli.isDisabledMaterialVariant ? (
                        <div css={Css.h100.df.fdc.aic.jcc.gap1.tac.ba.bcGray500.bgGray50.br4.$}>
                          <Tooltip title="Sometimes a product type isn't offered with certain Package Options (e.g., no refrigerator with the Essential package). In these cases, tell Design Packages to leave it empty!">
                            <Icon icon="remove" color={Palette.Gray700} inc={4} />
                          </Tooltip>
                          <div css={Css.gray700.mdSb.maxw75.$}>No Product</div>
                        </div>
                      ) : tli.materialVariant ? (
                        <div css={Css.relative.h100.w100.f1.br4.z1.$}>
                          <div
                            onClick={() => handleRedirect(tli, correctPlaceholderId)}
                            /* pseudo-element until :hover shows this. Overlays the image to darken it so Icons will pop */
                            className={productCardHoverImageOverlay}
                            css={
                              Css.absolute.cursorPointer.top0.left0.h100.w100
                                .bgColor("black")
                                .o0.z2.add("transition", "all 400ms").$
                            }
                          />
                          <img
                            alt="product-headline"
                            src={tli.materialVariant?.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
                            css={Css.absolute.h100.w100.p1.br4.objectCover.$}
                          />
                        </div>
                      ) : (
                        <div
                          onClick={() => handleRedirect(tli, correctPlaceholderId)}
                          css={Css.fg1.with(blueishFillableUx()).mb1.$}
                        >
                          <Icon icon="plus" pxSize={64} />
                        </div>
                      )}
                      <div
                        css={Css.df.fdc.gap1.oh.cursorPointer.$}
                        onClick={() => handleRedirect(tli, correctPlaceholderId)}
                      >
                        <div css={Css.xs.truncate.$}>{tli.customPlaceholderName || tli.name}</div>
                        {tli.materialVariant?.displayName && (
                          <div css={Css.sm.lineClamp(2).$}>{tli.materialVariant.displayName}</div>
                        )}
                      </div>
                      <div css={Css.fs0.df.fdc.aifs.gap1.w100.oxa.$}>
                        <OptionCombinationsChip tli={tli} />
                        {tli.targetPlan ? (
                          <Link to={createPlanPackageUrl(tli.targetPlan.id, tli.targetPlan.version.id)} target="_blank">
                            <Chip text={tli.targetPlan.name} icon="linkExternal" type="caution" />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {!readOnly && (
                    <div
                      css={Css.h100.w100.with(blueishFillableUx()).df.fdc.gap1.tac.$}
                      {...tid.newSubVariant}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigate(
                          createDesignPackageProductSearchUrl(
                            designPackage.id,
                            designPackage.version.id,
                            correctPlaceholderId,
                          ),
                        );
                      }}
                    >
                      <div css={Css.f1.df.fdc.jcc.gap1.$}>
                        <div css={Css.lg.$}>+ New Product Slot</div>
                        <div css={Css.xsSb.gray700.$}>e.g. Deluxe</div>
                      </div>
                      <div css={Css.fs0.df.fdr.p2.aic.gap1.w100.oxa.$}>
                        <OptionCombinationsChip tli={correctPlaceholder} showMissingCombinations />
                      </div>
                    </div>
                  )}
                </InlineGrid>
              </div>
            );
          })}
        </Fragment>
      ))}
      {!readOnly && (
        <InlineGrid>
          <div
            css={Css.h100.w100.with(blueishFillableUx()).df.fdc.jcc.gap1.tac.$}
            {...tid.newFamilyRow}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(createDesignPackageAddToSlotUrl(designPackage.id, designPackage.version.id));
            }}
          >
            <div css={Css.lg.$}>+ New {singleSlot?.slot.item.costCode?.name ?? "Product"} Slot</div>
            <div css={Css.xsSb.gray700.$}>e.g. 48" Gas Range</div>
          </div>
        </InlineGrid>
      )}
    </div>
  );
}

const blueishFillableUx = () => Css.ba.bsDashed.bgBlue50.bcBlue500.df.aic.jcsa.cursorPointer.blue400.$;
const InlineGrid = ({ children }: React.PropsWithChildren<unknown>) => (
  <div css={Css.dg.gtc("repeat(auto-fill, 276px)").gar("390px").gap2.$}>{children}</div>
);

/**
 * This handles all the filtering and sorting ahead of Rendering a list of TLIs. We only show ProductTLIs.
 *
 * Ordering TLIs has several considerations:
 *   1. Cluster related items together. For example, the [Kitchen Upgrade] cards should all appear side-by-side
 *   2. More complex cards should come after simpler cards: [No Upgrades] should come before [Kitchen Upgrade]
 *      which should come before [Kitchen Upgrade, Decorative Hood].
 *   3. Within a cluster, sort by the minimum order of the option within a group. This is a heuristic to try to
 *      put [Base, Premium] ahead of [Deluxe].
 *   4. PlaceholderProduct (no non-upgrade options) cards should come last within a cluster.
 */
function tliFilterAndSortToFinal<I extends DesignPackageConfiguratorSlotFragment["items"][number]>(tlis: I[]): I[] {
  return (
    tlis
      // Only show Product ITIs (which all point to their placeholder)
      .filter((tli) => tli.placeholder)
      // cluster tlis by Upgrade group via composite key: [rpog:1, rpog:2, rpog:3] -> "rpog:1-rpog:2-rpog:3"
      .groupByObject((tli) =>
        tli.options
          .map((rpo) => rpo.optionGroup.id)
          .unique()
          .sort() // fixes `rpog:1-rpog:2` vs `rpog:2-rpog:1`
          .join("-"),
      )
      // `""` then "rpog:1" then "rpog:1-rpog:2" then "rpog:1-rpog:2-rpog:3"
      .sortBy(([compositeRpogKey]) => compositeRpogKey.length)
      // map back down to clusters of [tli[], tli[], tli[]] that we can subsort before flattening
      .map(([, tlis]) => tlis)
      // with everything now sorted, flatten back down into 1 array. This is the final order.
      .flat()
  );
}
